import type {ReactElement} from 'react';
import React from 'react';
import BottomBar from '@Components/contextual-menu/components/bottom-bar';
import {openPosterEditorItemPositionModal} from '@Modals/poster-editor-item-position-modal/poster-editor-item-position-modal';
import {openPosterEditorItemFlipModal} from '@Modals/poster-editor-item-flip-modal';
import {openPosterEditorItemStretchModal} from '@Modals/poster-editor-item-stretch-modal';
import {openPosterEditorItemOpacityModal} from '@Modals/poster-editor-item-opacity-modal';
import {
  deleteItemByID,
  duplicateActiveItems,
  onCopyStylesClicked,
  showCopyStylesAsNewFeature,
  toggleLockStateForSelectedItem,
} from '@Components/poster-editor/library/poster-editor-library';
import {openPosterEditorQRContentModal} from '@Modals/poster-editor-qr-content-modal';
import {openPosterEditorQrContentColorModal} from '@Modals/poster-editor-qr-content-color-modal';
import {openPosterEditorQrContentBackgroundModal} from '@Modals/poster-editor-qr-content-background-modal';
import {useEditorPrefetchQRMobileModules} from '@Hooks/poster-editor/useEditorPrefetchQRMobileModules';
import {QRItemBottomBarOptions} from '@Components/poster-editor/components/poster-editor-bottom-bar/poster-editor-bottom-bar.types';
import {useAppSelector} from '@/hooks';
import {getCopiedStyles} from '@Components/poster-editor/poster-editor-reducer';
import {CopyStyleState} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-item-controls/poster-item-controls.types';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromActiveItem} from '@Hooks/poster-editor/useGetKeyFromActiveItem';
import {QrForegroundColor} from '@Components/poster-editor/components/poster-editor-bottom-bar/components/qr-foreground-color/qr-foreground-color';
import {PulsatingDot} from '@Components/poster-editor/components/pulsating-dot';
import styles from './qr-bottom-bar.module.scss';
import {openItemAnimationModal} from '@Modals/poster-editor-item-animation-modal';

export function QrBottomBar(): ReactElement<any> | null {
  const copiedStylesState = useAppSelector(getCopiedStyles) ? CopyStyleState.COPIED : CopyStyleState.COPY;
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const isLocked = useGetKeyFromActiveItem('lockMovementX');
  useEditorPrefetchQRMobileModules();

  if (!activeItem?.isQRItem()) {
    return null;
  }

  const pulsatingDotForCopyStyles = (): ReactElement<any> | null => {
    if (showCopyStylesAsNewFeature()) {
      return <PulsatingDot containerClassname={`_position-absolute  ${styles.pulsatingDotForCopyStyles}`} />;
    }
    return null;
  };

  const onClickHandler = (itemId: QRItemBottomBarOptions): void => {
    switch (itemId) {
      case QRItemBottomBarOptions.POSITION:
        openPosterEditorItemPositionModal();
        break;
      case QRItemBottomBarOptions.FLIP:
        openPosterEditorItemFlipModal();
        break;
      case QRItemBottomBarOptions.STRETCH:
        openPosterEditorItemStretchModal();
        break;
      case QRItemBottomBarOptions.COPY_STYLES:
        onCopyStylesClicked();
        break;
      case QRItemBottomBarOptions.OPACITY:
        openPosterEditorItemOpacityModal();
        break;
      case QRItemBottomBarOptions.LOCK:
        toggleLockStateForSelectedItem();
        break;
      case QRItemBottomBarOptions.DELETE:
        deleteItemByID(activeItem?.uid);
        break;
      case QRItemBottomBarOptions.DUPLICATE:
        duplicateActiveItems();
        break;
      case QRItemBottomBarOptions.QR_CONTENT:
        openPosterEditorQRContentModal();
        break;
      case QRItemBottomBarOptions.COLOR:
        openPosterEditorQrContentColorModal();
        break;
      case QRItemBottomBarOptions.BACKGROUND:
        openPosterEditorQrContentBackgroundModal();
        break;
      case QRItemBottomBarOptions.ANIMATION:
        openItemAnimationModal();
        break;
      default:
    }
  };

  const getCustomFillContent = (): ReactElement<any> => {
    return <QrForegroundColor />;
  };

  return (
    <BottomBar
      id="poster-editing-default-bottom-bar"
      onClick={onClickHandler}
      items={[
        {
          id: QRItemBottomBarOptions.QR_CONTENT,
          icon: 'icon-qr-content',
          text: window.i18next.t('pmwjs_qr_content'),
          textClasses: 'spacing-p-0',
        },
        {
          id: QRItemBottomBarOptions.COLOR,
          customContent: getCustomFillContent(),
          text: window.i18next.t('pmwjs_color'),
        },
        {
          id: QRItemBottomBarOptions.BACKGROUND,
          icon: 'icon-background',
          text: window.i18next.t('pmwjs_background'),
        },
        {
          id: QRItemBottomBarOptions.DUPLICATE,
          icon: 'icon-copy',
          text: window.i18next.t('pmwjs_duplicate'),
        },
        {
          id: QRItemBottomBarOptions.POSITION,
          icon: 'icon-prints-stack',
          text: window.i18next.t('pmwjs_position'),
        },
        {
          id: QRItemBottomBarOptions.FLIP,
          icon: 'icon-flip-horizontal',
          text: window.i18next.t('pmwjs_flip'),
        },
        {
          id: QRItemBottomBarOptions.STRETCH,
          icon: 'icon-fit-to-width',
          text: window.i18next.t('pmwjs_stretch'),
        },
        {
          id: QRItemBottomBarOptions.COPY_STYLES,
          icon: copiedStylesState === CopyStyleState.COPY ? 'icon-copy-style-brush' : 'icon-paste-style-brush',
          text: copiedStylesState === CopyStyleState.COPY ? window.i18next.t('pmwjs_copy_style') : window.i18next.t('pmwjs_paste_style'),
          isSelected: copiedStylesState === CopyStyleState.COPIED,
          tag: pulsatingDotForCopyStyles(),
        },
        {
          id: QRItemBottomBarOptions.OPACITY,
          icon: 'icon-opacity',
          text: window.i18next.t('pmwjs_opacity'),
        },
        {
          id: QRItemBottomBarOptions.ANIMATION,
          icon: 'icon-animation',
          text: window.i18next.t('pmwjs_animation'),
        },
        {
          id: QRItemBottomBarOptions.LOCK,
          icon: isLocked ? 'icon-lock' : 'icon-unlock',
          text: isLocked ? window.i18next.t('pmwjs_unlock') : window.i18next.t('pmwjs_lock'),
        },
        {
          id: QRItemBottomBarOptions.DELETE,
          icon: 'icon-delete',
          text: window.i18next.t('pmwjs_delete'),
          className: 'color-danger-100',
        },
      ]}
    />
  );
}
